import { difficultyLevelData, DifficultyLevelType, GameName } from 'constants/difficulty';
import { pad } from 'game/helpers/format';
import { StoreLocalStorage } from './storage';
import { TABLET_WIDTH_BREAK_POINT } from 'constants/layout';
import { Layout } from 'game/LayoutManager';
import { Card } from 'components/Card';
import { SolitaireGame } from 'game/SolitaireGame';
import { MoveData } from 'game/Moves';
import { GameEvent } from 'game/events/gameEvent';
import { EventName } from 'game/events/eventsName';
import { freeCellGameState, spiderGameState, KlondikeMockStore } from './mock';
import { GAEvent, GAEventNames } from 'game/analytics/events';
import { valuesIn } from 'lodash';
import { theme } from 'game/theme';
import { fixedGameDifficulty } from './fixedGameDifficulty';
import { GameLog } from 'game/helpers/debug';
import { MissionContext, MissionContextData, MissionState } from 'game/quests/types';
import { deepMerge } from './utils';
import { getDesignParamsFromURL, hasDesignParams, removeDesignParamsFromURL } from 'game/utils/urlParams';
import { preloadGameSounds } from 'game/Sounds';

export const DEFAULT_HINT_COUNTER = 2;
export const DEFAULT_UNDO_COUNTER = 10; //unlimited
export const DEFAULT_WAND_COUNTER = 2;
export const ADS_RECEIVE_WAND_COUNTER = 1;

const FEEDBACK_FIRST_SHOW = 15;
const FEEDBACK_NEXT_SHOW = 20;
// const FEEDBACK_SHOW_LIMIT = 10;

const FACEBOOK_FIRST_SHOW = 10;
export const FACEBOOK_NEXT_POPUP_SHOW = 20;
export const FACEBOOK_SHOW_LIMIT = 5;

const NOTIFICATION_FIRST_SHOW = 5;
export const NOTIFICATION_NEXT_POPUP_SHOW = 20;
export const NOTIFICATION_SHOW_LIMIT = 999999;

export const TICKETS_SHOW_LIMIT = 50;
export const TICKETS_NEXT_SHOW = 50;
export type DealData = { id: string; deal: 'string' };
export const SCORE_NA = -999;

const defaultTheme = {
    front: '11_white_large',
    back: '71_classic_blue',
    background: '1_green',
};

export type GameWinScoreData = {
    minMoves: number;
    maxMoves: number;
    medianMoves: number;
    minTime: number;
    maxTime: number;
    medianTime: number;
};
export const emptyWinScore = {
    minMoves: SCORE_NA,
    maxMoves: SCORE_NA,
    medianMoves: SCORE_NA,
    minTime: SCORE_NA,
    maxTime: SCORE_NA,
    medianTime: SCORE_NA,
} as GameWinScoreData;
const defaultSettingsMobile = {
    soundOn: false,
    autocomplete: true,
    leftHanded: false,
    cardHighlighting: true,
    autoPlay: true,
};
const defaultSettingsDesktop = {
    soundOn: false,
    autocomplete: true,
    leftHanded: true,
    cardHighlighting: true,
    autoPlay: true,
};
type GameData = {
    columns: { code: string; visible: boolean }[][];
    foundation: { code: string; visible: boolean }[][];
    stockpile: { code: string; visible: boolean }[];
    wastePile: { code: string; visible: boolean }[];
    freeCells: { code: string; visible: boolean }[][];
    gameId: string;
    name: string;
    score: number;
    time: number;
    movesCount: number;
    moves: MoveData[][]; //for undo
    attempts: number;
    magicWandUsed: number;
    gameStarted: boolean;
};
const emptyLastGame = {
    columns: [] as { code: string; visible: boolean }[][],
    foundation: [] as { code: string; visible: boolean }[][],
    stockpile: [] as { code: string; visible: boolean }[],
    wastePile: [] as { code: string; visible: boolean }[],
    freeCells: [] as { code: string; visible: boolean }[][],
    gameId: '',
    name: '',
    score: 0,
    time: 0,
    movesCount: 0,
    moves: [] as MoveData[][], //for undo
    attempts: 0,
    magicWandUsed: 0,
    gameStarted: false,
} as GameData;
export class GameStore {
    tutorialViewed = {
        [GameName.KLONDIKE]: false,
        [GameName.SPIDER]: false,
        [GameName.FREECELL]: false,
    };
    difficultyStarted = [] as DifficultyLevelType[];
    difficulty = DifficultyLevelType.KLONDIKE_EASY_1CARD;
    difficultyBalanced = true;
    difficultyBalancedGames = [] as string[];
    difficultyBalancedIndex = -1;
    dealCount = 1;
    suits = 1;
    movesCount = 0;
    score = 0;
    startTime = 0;
    stopTime = 0;
    gameActive = false;
    language = 'en';
    front = defaultTheme.front;
    back = defaultTheme.back;
    background = defaultTheme.background;
    gameId = '';
    isMobile = false;
    soundOn = false;
    autocomplete = false;
    leftHanded = false;
    cardHighlighting = true;
    autoPlay = false;
    webPSupported = false;
    isFeedbackBlocked = false;

    gameStorage: StoreLocalStorage;
    undos = 0;
    hints = 0;
    deckTurns = 0;

    undoCounter = 0;
    hintCounter = 0;
    wandCounter = 0;

    attempts = 0;
    wins = 0;
    gameWins = {} as { [key in GameName]: number };
    name = GameName.KLONDIKE;
    previousGame = {
        difficulty: '', // not work for Random 3
        gameId: '',
        name: '', //klondike / spider
    };
    gameState = emptyLastGame;
    tutorialState = emptyLastGame;
    moves = [] as MoveData[][];
    facebookWinPopupNextShow = -1;
    notificationIcons = ['share', 'design', 'rules', 'settings'];
    gameOver = false;
    noAds = false;
    gameStarted = false;
    infoPopupShown = [] as string[];
    magicWandUsed = 0;
    feedbackCounterShown = 0;
    notificationWinPopupNextShow = NOTIFICATION_FIRST_SHOW;

    private secondAssetPackLoaded = false;
    triggeredGameStarted = false;
    lowPerformanceDevice = true;
    popupLimits = {};

    availableGamesToTutorial = [GameName.KLONDIKE, GameName.SPIDER, GameName.FREECELL];
    deals = {
        [DifficultyLevelType.KLONDIKE_MEDIUM_3CARDS]: [],
        [DifficultyLevelType.KLONDIKE_HARD_3CARDS]: [],
        [DifficultyLevelType.KLONDIKE_EXPERT_3CARDS]: [],

        [DifficultyLevelType.KLONDIKE_EASY_1CARD]: [],
        [DifficultyLevelType.KLONDIKE_MEDIUM_1CARD]: [],
        [DifficultyLevelType.KLONDIKE_HARD_1CARD]: [],
        [DifficultyLevelType.KLONDIKE_EXPERT_1CARD]: [],

        [DifficultyLevelType.SPIDER_EASY_1SUIT]: [],
        [DifficultyLevelType.SPIDER_MEDIUM_1SUIT]: [],
        [DifficultyLevelType.SPIDER_EXPERT_1SUIT]: [],
        [DifficultyLevelType.SPIDER_HARD_1SUIT]: [],
        [DifficultyLevelType.SPIDER_MEDIUM_2SUIT]: [],
        [DifficultyLevelType.SPIDER_HARD_2SUIT]: [],
        [DifficultyLevelType.SPIDER_EXPERT_2SUIT]: [],
        [DifficultyLevelType.SPIDER_EXPERT_4SUIT]: [],
        [DifficultyLevelType.SPIDER_HARD_4SUIT]: [],

        [DifficultyLevelType.FREECELL_EASY]: [],
        [DifficultyLevelType.FREECELL_MEDIUM]: [],
        [DifficultyLevelType.FREECELL_HARD]: [],
        [DifficultyLevelType.FREECELL_EXPERT]: [],

        [DifficultyLevelType.PYRAMID_EASY]: [],
    } as { [key in DifficultyLevelType]: DealData[] };
    storiesViewed = false;
    storiesNextPopup = 0;
    gamesWinScore = null as null | GameWinScoreData;
    tutorialActive = false;
    tutorialGame = null as null | GameName;
    inArticle = false;
    tutorialStep = 0;
    tutorialWelcomeScreenShowedCount = 0;
    gameStateBeforeTutorial = emptyLastGame;
    constructor() {
        this.init();
    }

    private events: Array<string> = [];

    private init() {
        this.mockDeal = this.mockDeal.bind(this);
        this.removeAds = this.removeAds.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.showFeedbackOnWin = this.showFeedbackOnWin.bind(this);

        this.setGameName(this._getGameName() || GameName.KLONDIKE);

        this.gameStorage = new StoreLocalStorage();
        
        this.loadStorage();

        addEventListener('mockDeal', this.mockDeal, false);
        addEventListener('mockWins', this.mockWins.bind(this), false);
        addEventListener('noAds', this.removeAds, false);
        addEventListener('closeModal', this.closeModal, false);
        this.events.push(GameEvent.add(EventName.ON_VISIBILITY_CHANGE, this.onVisibleChange.bind(this)));
        this.events.push(GameEvent.add(EventName.UI_ASSETS_LOADED, () => {
            this.secondAssetPackLoaded = true;
        }));
        this.checkDevicePerformance();
    }

    reInit() {
        removeEventListener('mockDeal', this.mockDeal, false);
        removeEventListener('mockWins', this.mockWins.bind(this), false);
        removeEventListener('noAds', this.removeAds, false);
        removeEventListener('closeModal', this.closeModal, false);

        this.events.forEach(event => {
            GameEvent.remove(event);
        });

        this.init();
    }
    
    checkDevicePerformance() {
        function runPerformanceTest() {
            const start = performance.now();
            for (let i = 0; i < 1e7; i++) {} // Tight loop
            const end = performance.now();
            const duration = end - start;
            return duration;
        }

        function isLowEndDevice() {
            const cores = navigator.hardwareConcurrency || 3;
            const memory = navigator.deviceMemory || 3;
            const perf = runPerformanceTest();

            return cores <= 2 || memory <= 2 || perf > 200;
        }

        this.lowPerformanceDevice = isLowEndDevice();
    }
    get assetLoaded() {
        return this.secondAssetPackLoaded;
    }

    closeModal(e: CustomEvent) {
        GameLog('closeModal', e.detail);
        GameEvent.emit(EventName.UNBLOCK_KEYBOARD);
        this.resumeGame();
        GameEvent.emit(EventName.CLOSE_MODAL, e.detail);
    }
    removeAds() {
        this.undoCounter = 9;
        this.hintCounter = 9;
        this.wandCounter = 9;
        this.gameStorage.setItem('undoCounter', 99);
        this.gameStorage.setItem('hintCounter', 99);
        this.gameStorage.setItem('wandCounter', 99);
        this.noAds = true;
    }
    blockFacebookWinPopup() {
        Store.facebookWinPopupNextShow = -1;
        this.gameStorage.setItem('facebookWinPopupNextShow', this.facebookWinPopupNextShow);
    }

    initGameType() {
        this.dealCount = this._getInitialDraw();
        this.suits = this._getInitialSuits();
    }

    async loadStorage() {
        const isLoaded = await this.gameStorage.isSet();
        await this._checkTutorialActive();

        this.initGameType();

        this.tutorialViewed = await this.gameStorage.getItem('tutorialViewed', this.tutorialViewed);
        this.tutorialStep = 0;
        // this.tutorialStep = await this.gameStorage.getItem('tutorialStep', 0);\
        this.previousGame = await this.gameStorage.getItem('previousGame', {});
        this.tutorialWelcomeScreenShowedCount = parseInt(await this.gameStorage.getItem('tutorialWelcomeScreenShowedCount', '0'));
        this.difficultyStarted = await this.gameStorage.getItem('difficultyStarted', []);
        this.difficultyBalanced = await this.gameStorage.getItem('difficultyBalanced', true);
        this.isFeedbackBlocked = await this.gameStorage.getItem('feedbackBlocked', false);
        this.difficultyBalancedGames = await this.gameStorage.getItem('difficultyBalancedGames', []);
        this.difficultyBalancedIndex = parseInt(await this.gameStorage.getItem('difficultyBalancedIndex', '-1'));

        const difficulty = await this._getInitialDifficulty();

        this.difficulty = difficulty;
        this.gameState = await this.gameStorage.getItem(`gameState_${this.name}_${this.difficulty}`, this.gameState);
        this.gameStateBeforeTutorial = await this.gameStorage.getItem(
            `gameState_${this.name}_${this.difficulty}`,
            this.gameState
        );
        if (this.gameState.gameId) {
            this.previousGame = {
                difficulty: this.difficulty,
                gameId: this.gameState.gameId,
                name: this.gameState.name,
            };
        }

        const displayMobile = window.innerWidth < TABLET_WIDTH_BREAK_POINT;
        if (displayMobile) {
            try {
                const userAgent = navigator.userAgent || navigator.vendor || window.opera;
                if (/android/i.test(userAgent)) this.isMobile = true;
                if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) this.isMobile = true;
            } catch (error) {}
        }
        const fallBack = this.isMobile ? defaultSettingsMobile : defaultSettingsDesktop;
        const storeValue = (status: boolean) => {
            return status ? 'ON' : 'OFF';
        };
        this.soundOn = (await this.gameStorage.getItem('soundOn', storeValue(fallBack.soundOn))) === 'ON';
        this.storiesViewed = localStorage.getItem('stories_tutorial') === 'shown';
        this.storiesNextPopup = parseInt(await this.gameStorage.getItem('storiesNextPopup', '0'));

        this.autocomplete =
            (await this.gameStorage.getItem('autocomplete', storeValue(fallBack.autocomplete))) === 'ON';
        this.leftHanded = (await this.gameStorage.getItem('leftHanded', storeValue(fallBack.leftHanded))) === 'ON';

        const cardHighlightingStored = (await this.gameStorage.getItem('cardHighlighting', storeValue(fallBack.cardHighlighting)));

        this.cardHighlighting = cardHighlightingStored === 'ON' || cardHighlightingStored === true;
        this.autoPlay = (await this.gameStorage.getItem('autoPlay', storeValue(fallBack.autoPlay))) === 'ON';
        this.wins = parseInt(await this.gameStorage.getItem('wins', '0'));

        const gameWins = await this.gameStorage.getItem('gameWins', {});
        Object.keys(gameWins).forEach((key) => {
            this.gameWins[key as GameName] = parseInt(gameWins[key]);
        });

        this.notificationIcons = await this.gameStorage.getItem('notificationIcons', this.notificationIcons);

        if (isLoaded) {
            this.front = await this.gameStorage.getItem('front', this.front);
            this.back = await this.gameStorage.getItem('back', this.back);
            this.background = await this.gameStorage.getItem('background', this.background);

            this.undoCounter = await this.gameStorage.getItem('undoCounter', DEFAULT_HINT_COUNTER);
            this.hintCounter = await this.gameStorage.getItem('hintCounter', DEFAULT_UNDO_COUNTER);
            this.wandCounter = await this.gameStorage.getItem('wandCounter', DEFAULT_WAND_COUNTER);
            this.infoPopupShown = await this.gameStorage.getItem('infoPopupShown', []);
            this.feedbackCounterShown = await this.gameStorage.getItem('feedbackCounterShown', 0);

            this.notificationWinPopupNextShow = await this.gameStorage.getItem(
                'notificationWinPopupNextShow',
                NOTIFICATION_FIRST_SHOW
            );
            this.facebookWinPopupNextShow = await this.gameStorage.getItem(
                'facebookWinPopupNextShow',
                FACEBOOK_FIRST_SHOW
            );

            if (this.notificationWinPopupNextShow != -1 && this.notificationWinPopupNextShow < NOTIFICATION_FIRST_SHOW)
                this.notificationWinPopupNextShow = NOTIFICATION_FIRST_SHOW;
            if (this.facebookWinPopupNextShow != -1 && this.facebookWinPopupNextShow < FACEBOOK_FIRST_SHOW)
                this.facebookWinPopupNextShow = FACEBOOK_FIRST_SHOW;
        } else {
            this.gameStorage.setItem('version', this.gameStorage.currentVersion);
            this.gameStorage.setItem('soundOn', this.soundOn ? 'ON' : 'OFF');
            this.gameStorage.setItem('autocomplete', this.autocomplete ? 'ON' : 'OFF');
            this.gameStorage.setItem('leftHanded', this.leftHanded ? 'ON' : 'OFF');
            this.gameStorage.setItem('cardHighlighting', this.cardHighlighting ? 'ON' : 'OFF');
            this.gameStorage.setItem('autoPlay', this.autoPlay ? 'ON' : 'OFF');

            this.setHintCounter(DEFAULT_HINT_COUNTER);
            this.setUndoCounter(DEFAULT_UNDO_COUNTER);
            this.setWandCounter(DEFAULT_WAND_COUNTER);
        }

        const facebookPopupValue = await this.gameStorage.getItem('facebookWinPopupNextShow', undefined);
        if (!facebookPopupValue) {
            this.facebookWinPopupNextShow = FACEBOOK_FIRST_SHOW;
            this.gameStorage.setItem('facebookWinPopupNextShow', this.facebookWinPopupNextShow);
        }

        // Apply design from URL GET parameters if present
        this.applyDesignFromURLParams();

        this.checkGameAssetsStorage();
        this.setBack(this.back);
        this.setFront(this.front);
        this.setBackground(this.background);
    }
    setGameName(gameName: GameName) {
        this.name = gameName;
    }
    checkGameAssetsStorage() {
        if (!theme.fronts.includes(this.front)) this.front = defaultTheme.front;
        if (!theme.backs.includes(this.back)) this.back = defaultTheme.back;
        if (!theme.backgrounds.includes(this.background)) this.background = defaultTheme.background;
    }

    /**
     * Apply design settings from URL GET parameters
     * If parameters are present, they override localStorage values
     * Parameters are removed from URL after being applied
     */
    applyDesignFromURLParams() {
        if (!hasDesignParams()) {
            return;
        }

        const params = getDesignParamsFromURL();
        let hasChanges = false;

        // Apply background if present and valid
        if (params.background && theme.backgrounds.includes(params.background)) {
            this.background = params.background;
            this.gameStorage.setItem('background', params.background);
            hasChanges = true;
            console.log('Applied background from URL:', params.background);
        }

        // Apply card back if present and valid
        if (params.cardBack && theme.backs.includes(params.cardBack)) {
            this.back = params.cardBack;
            this.gameStorage.setItem('back', params.cardBack);
            hasChanges = true;
            console.log('Applied card back from URL:', params.cardBack);
        }

        // Apply card front if present and valid
        if (params.cardFront && theme.fronts.includes(params.cardFront)) {
            this.front = params.cardFront;
            this.gameStorage.setItem('front', params.cardFront);
            hasChanges = true;
            console.log('Applied card front from URL:', params.cardFront);
        }

        // Remove GET parameters from URL after applying
        if (hasChanges) {
            removeDesignParamsFromURL();
            console.log('Design parameters applied and removed from URL');
        }
    }
    private _getGameName() {
        const gameName = document.getElementById('game-container')?.getAttribute('data-name');
        if (gameName) {
            switch (gameName) {
                case GameName.KLONDIKE:
                case GameName.SPIDER:
                case GameName.FREECELL:
                case GameName.PYRAMID:
                    return gameName;
            }
        }
        return null;
    }

    private _getInitialDraw() {
        try {
            // If tutorial is active, always read from data attribute without saving
            if (this.tutorialActive) {
                const draw = document.getElementById('game-container')?.getAttribute('data-draw');
                if (draw) {
                    return parseInt(draw);
                }
                return 1;
            }

            const saved = localStorage.getItem(`${this.name}_last_draw`);
            if (saved) {
                return parseInt(saved);
            } else {
                const draw = document.getElementById('game-container')?.getAttribute('data-draw');
                if (draw) {
                    localStorage.setItem(`${this.name}_last_draw`, draw);
                    return parseInt(draw);
                }
            }
        } catch {}

        localStorage.setItem(`${this.name}_last_draw`, '1');
        return 1;
    }

    private _getInitialSuits() {
        try {
            // If tutorial is active, always read from data attribute without saving
            if (this.tutorialActive) {
                const suitAttr = document.getElementById('game-container')?.getAttribute('data-suit');
                if (suitAttr) {
                    return parseInt(suitAttr);
                }
                return 1;
            }

            const saved = localStorage.getItem(`${this.name}_last_suits`);
            if (saved) {
                return parseInt(saved);
            } else {
                const suitAttr = document.getElementById('game-container')?.getAttribute('data-suit');
                if (suitAttr) {
                    localStorage.setItem(`${this.name}_last_suits`, suitAttr);
                    return parseInt(suitAttr);
                }
            }
        } catch {}

        localStorage.setItem(`${this.name}_last_suits`, '1');
        return 1;
    }

    private async _checkTutorialActive() {
        this.tutorialGame = await this.gameStorage.getItem('tutorialGame', null);

        let inArticle = document.getElementById('game-container')?.getAttribute('data-tutorial') === 'true'; //boolean
        if (inArticle) {
            this.tutorialActive = true;
            this.tutorialGame = this.name;
            this.inArticle = true;
        } else {
            if (this.tutorialGame !== this.name) {
                this.tutorialActive = false;
                this.tutorialGame = null;
                this.gameStorage.setItem('tutorialGame', null);
                this.gameStorage.setItem('tutorialActive', false);
            } else {
                this.tutorialActive = false;
                // this.tutorialActive = await this.gameStorage.getItem('tutorialActive', false);
            }
        }
        this.tutorialState = await this.gameStorage.getItem(`tutorialState`, this.tutorialState);
    }

    private async _getInitialDifficulty() {
        let attrDifficulty = document.getElementById('game-container')?.getAttribute('data-difficulty'); //easy, medium, ect.
        let fixed = document.getElementById('game-container')?.getAttribute('data-difficulty-fixed') === 'true'; //easy, medium, ect.
        if (fixed && attrDifficulty) {
            return fixedGameDifficulty(attrDifficulty);
        }
        if (!fixed) {
            const previousGame = this.getPreviousGame();
            if (previousGame && previousGame.name === this.name) {
                return previousGame.difficulty;
            }
            const difficultyStorage = await this.gameStorage.getItem('difficulty_' + this.getStoreName(), undefined);
            if (difficultyStorage && !fixed) return difficultyStorage;
        }
        if (this.isKlondike) {
            let attrDraw = document.getElementById('game-container')?.getAttribute('data-draw'); //1 , 3
            if (attrDifficulty && attrDraw) {
                attrDifficulty = attrDifficulty.toUpperCase();
                let dif = valuesIn(DifficultyLevelType).find((value) => value === `${attrDifficulty}_${attrDraw}`);
                if (!dif) {
                    dif =
                        attrDraw === '1'
                            ? DifficultyLevelType.KLONDIKE_EASY_1CARD
                            : DifficultyLevelType.KLONDIKE_MEDIUM_3CARDS;
                }
                return dif;
            }
            return DifficultyLevelType.KLONDIKE_MEDIUM_3CARDS;
        }
        if (this.isSpider) {
            switch (this._getInitialSuits()) {
                case 1:
                    return DifficultyLevelType.SPIDER_EASY_1SUIT;
                case 2:
                    return DifficultyLevelType.SPIDER_HARD_2SUIT;
                case 4:
                    return DifficultyLevelType.SPIDER_EXPERT_4SUIT;
            }
        }
        if (this.isFreecell) {
            return DifficultyLevelType.FREECELL_EASY;
        }
        if (this.isPyramid) {
            return DifficultyLevelType.PYRAMID_EASY;
        }

        return DifficultyLevelType.KLONDIKE_MEDIUM_3CARDS;
    }
    getStoreName() {
        let name = this.name as string;
        switch (this.name) {
            case GameName.KLONDIKE:
                name += this.dealCount;
                break;
            case GameName.SPIDER:
                name += this.suits;
                break;
            case GameName.PYRAMID:
                const diff = this.difficulty.toLowerCase(); // 'pyramid_easy'
                name = diff + '_1';
                console.log('[PYRAMID] getStoreName id:', name);
                break;
        }
        return name;
    }
    getStoreNameByType(name: string, dealCount: number, suits: number) {
        let result = name;
        switch (name) {
            case GameName.KLONDIKE:
                result += dealCount;
                break;
            case GameName.SPIDER:
                result += suits;
                break;
        }
        return result;
    }
    getGameTypeName(value: DifficultyLevelType) {
        const gameNameParts = value.split('_');
        if (gameNameParts[2]) return gameNameParts[0] + '_' + gameNameParts[2];
        return gameNameParts[0];
    }
    setDifficulty(value: DifficultyLevelType, balanced = true) {
        GameLog('setDifficulty', value, balanced);
        
        if (!value || typeof value !== 'string') {
            console.error('setDifficulty: Invalid value provided:', value);
            return;
        }
        
        const gameTypeName = Store.getGameTypeName(value);
        if (balanced) {
            if (!Store.difficultyBalancedGames.includes(gameTypeName)) Store.difficultyBalancedGames.push(gameTypeName);
        } else {
            Store.difficultyBalancedGames = Store.difficultyBalancedGames.filter((item) => item !== gameTypeName);
        }
        if (!balanced) Store.difficultyBalancedIndex = -1;
        Store.difficultyBalanced = balanced;
        this.gameStorage.setItem('difficultyBalancedGames', Store.difficultyBalancedGames);
        this.gameStorage.setItem('difficultyBalanced', balanced);
        this.gameStorage.setItem('difficultyBalancedIndex', Store.difficultyBalancedIndex);
        this.gameStorage.setItem('difficulty_' + this.getStoreName(), value);
        this.difficulty = value;
    }
    setHintCounter(value: number) {
        if (this.noAds) return;
        this.hintCounter = value;
        this.gameStorage.setItem('hintCounter', value);
        GameEvent.emit(EventName.UI_UPDATE_COUNTERS);
    }
    setUndoCounter(value: number) {
        if (this.noAds) return;

        this.undoCounter = value;
        this.gameStorage.setItem('undoCounter', value);
    }
    setWandCounter(value: number) {
        if (this.noAds) return;
        this.wandCounter = value;
        this.gameStorage.setItem('wandCounter', value);
        GameEvent.emit(EventName.UI_UPDATE_COUNTERS);
    }

    getDifficultyText(difficulty?: DifficultyLevelType) {
        if (this.difficultyBalanced) return 'balanced';

        difficulty = difficulty || this.difficulty;
        
        if (!difficulty || !difficultyLevelData[difficulty]) {
            return 'unknown';
        }
        
        return difficultyLevelData[difficulty].type;
    }
    getGameTypeText() {
        if (this.isKlondike) return this.getDealCountText();
        if (this.isSpider) return this.getSuitCountText();
        if (this.isPyramid) return 'Pyramid';
        return 'not set';
    }

    getDealCountText(prevDiff?: DifficultyLevelType) {
        const diff = prevDiff || this.difficulty;

        if (!diff || typeof diff !== 'string') {
            return '1 card';
        }

        if (diff.includes('3')) return '3 cards';
        else return '1 card';
    }
    getSuitCountText(prevSuit?: DifficultyLevelType) {
        const diff = prevSuit || this.difficulty;
        
        if (!diff || typeof diff !== 'string') {
            return '1 suit';
        }
        
        if (diff.includes('1')) return '1 suit';
        if (diff.includes('2')) return '2 suits';
        return '4 suits';
    }
    get time() {
        const now = performance.now();
        const rawMs = this.gameActive ? now - this.startTime : this.stopTime - this.startTime;
        const safeMs = Math.max(rawMs, 0);
        return Math.floor(safeMs / 1000);
    }
    get timeMin() {
        const now = performance.now();
        const rawMs = this.gameActive ? now - this.startTime : this.stopTime - this.startTime;
        const safeMs = Math.max(rawMs, 0);
        return Math.floor(safeMs / 1000 / 60);
    }
    getTimeFormat(timeInSec = 0) {
        const secInMinute = 60;
        const minutes = Math.floor(timeInSec / secInMinute).toString(); // Time in minutes
        const seconds = (timeInSec % secInMinute).toString(); // Time in seconds
        return `${pad(minutes)}:${pad(seconds)}`;
    }
    startGame() {
        if (!this.triggeredGameStarted) {
            dispatchEvent(new CustomEvent('gameStarted'));
            this.triggeredGameStarted = true;
        }
        if (this.gameStarted) return;
        this.startTime = performance.now();
        this.gameActive = true;
        this.gameStarted = true;

        const newGameEventData = {
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
            hand: Store.leftHanded ? 'Left' : 'Right',
            background: Store.background, //1_green
            card_back: Store.back,
            card_front: Store.front,
            sound: Store.soundOn ? 'On' : 'Off',
        } as { [key: string]: string };
        switch (Store.name) {
            case GameName.KLONDIKE:
                newGameEventData.autoPlay = Store.autoPlay ? 'On' : 'Off';

                break;
            case GameName.SPIDER:
                newGameEventData.highlighting = Store.cardHighlighting ? 'On' : 'Off';
                break;
            case GameName.FREECELL:
                newGameEventData.highlighting = Store.cardHighlighting ? 'On' : 'Off';
                newGameEventData.autoPlay = Store.autoPlay ? 'On' : 'Off';
                break;
            case GameName.PYRAMID:
                newGameEventData.highlighting = Store.cardHighlighting ? 'On' : 'Off';
                break;
        }

        GAEvent(GAEventNames.GAME_START, newGameEventData);
    }
    winGame() {
        Store.gameStarted = false;
        this.gameOver = true;
        this.wins++;
        this.gameStorage.setItem('wins', this.wins);
        this.gameWins[this.name] = this.gameWins[this.name] ? this.gameWins[this.name] + 1 : 1;

        this.gameStorage.setItem('gameWins', this.gameWins);
        this.stopTime = performance.now();
        this.gameActive = false;
        Store.increaseContextValue('wins');
    }
    pauseGame() {
        if (!this.gameActive) return;
        this.stopTime = performance.now();
        this.gameActive = false;
    }
    resumeGame() {
        if (this.gameActive) return;
        if (!this.gameStarted) return;
        if (this.stopTime > 0) {
            const delta = performance.now() - this.stopTime;
            if (delta > 0) this.startTime += delta;
            this.stopTime = 0;
        } else {
            // No recorded pause timestamp; ensure we don't introduce negative elapsed
            this.startTime = Math.min(this.startTime, performance.now());
        }
        this.gameActive = true;
    }
    reset() {
        this.gameOver = false;
        this.movesCount = 0;
        this.startTime = 0;
        this.stopTime = 0;
        this.gameActive = false;
        this.gameStarted = false;
        this.undos = 0;
        this.hints = 0;
        this.deckTurns = 0;
        this.score = 0;
        if (this.isSpider) {
            this.score = 500;
        }
    }

    toggleSound() {
        this.soundOn = !this.soundOn;
        this.gameStorage.setItem('soundOn', this.soundOn ? 'ON' : 'OFF');
        
        // Load sounds when user enables sound for the first time
        if (this.soundOn) {
            preloadGameSounds();
        }
        
        return this.soundOn;
    }
    toggleAutocomplete() {
        this.autocomplete = !this.autocomplete;
        this.gameStorage.setItem('autocomplete', this.autocomplete ? 'ON' : 'OFF');
        return this.autocomplete;
    }
    toggleLeftHanded() {
        this.leftHanded = !this.leftHanded;
        this.gameStorage.setItem('leftHanded', this.leftHanded ? 'ON' : 'OFF');
        return this.leftHanded;
    }
    toggleCardHighlighting() {
        this.cardHighlighting = !this.cardHighlighting;
        this.gameStorage.setItem('cardHighlighting', this.cardHighlighting ? 'ON' : 'OFF');
    }
    toggleAutoPlay() {
        this.autoPlay = !this.autoPlay;
        this.gameStorage.setItem('autoPlay', this.autoPlay ? 'ON' : 'OFF');
    }
    setFront(frontFile: string) {
        this.front = frontFile;
        this.gameStorage.setItem('front', frontFile);
    }
    setBack(back: string) {
        this.back = back;
        this.gameStorage.setItem('back', back);
    }
    setBackground(background: string) {
        this.background = background;
        this.gameStorage.setItem('background', background);
    }

    getPreviousGame() {
        return this.previousGame;
    }
    getPreviousGameId(difficulty: DifficultyLevelType) {
        let fixed = document.getElementById('game-container')?.getAttribute('data-difficulty-fixed') === 'true';
        if (fixed) return null;

        if (!this.previousGame) return null;
        if (!this.gameState.gameId) return null;

        if (this.name === this.previousGame.name && this.previousGame.difficulty === difficulty)
            return this.previousGame.gameId;
        return null;
    }
    setPreviousGame() {
        const gameTypeName = Store.getGameTypeName(this.difficulty);

        if (Store.difficultyBalancedGames.includes(gameTypeName)) {
            Store.difficultyBalanced = true;
        } else {
            Store.difficultyBalanced = false;
        }
        this.gameStorage.setItem('difficultyBalanced', Store.difficultyBalanced);
        this.previousGame = {
            difficulty: this.difficulty,
            gameId: this.gameId,
            name: this.name,
        };
        this.gameStorage.setItem('previousGame', this.previousGame);
        if (this.difficultyStarted.includes(this.difficulty)) return;
        this.difficultyStarted.push(this.difficulty);
        this.gameStorage.setItem('difficultyStarted', this.difficultyStarted);
    }
    getDealCount() {
        switch (this.name) {
            case GameName.KLONDIKE:
                return this.dealCount;
            case GameName.SPIDER:
                return Layout.columns;
            case GameName.FREECELL:
                return Layout.columns;
            case GameName.PYRAMID:
                return this.dealCount;
        }
    }
    get isSpider() {
        return this.name === GameName.SPIDER;
    }
    get isFreecell() {
        return this.name === GameName.FREECELL;
    }
    get isKlondike() {
        return this.name === GameName.KLONDIKE;
    }
    get isPyramid() {
        return this.name === GameName.PYRAMID;
    }
    setGameState(game: SolitaireGame) {
        function mapTableCard(cards: Card[]) {
            return cards.map((card) => card ? ({ code: card.code, visible: card.visible }) : null);
        }
        const state = {
            gameId: this.gameId,
            columns: [...game.columns].map((column) => mapTableCard(column.cards)),
            foundation: [...game.foundations].map((column) => mapTableCard(column.cards)),
            name: this.name,
            movesCount: this.movesCount,
            moves: [],
            attempts: this.attempts,
            score: this.score,
            time: this.startTime - performance.now(),
            magicWandUsed: this.magicWandUsed,
            gameStarted: this.gameStarted,
        } as any;
        const mapKey = this.tutorialActive ? 'tutorialState' : `gameState_${this.name}_${this.difficulty}`;
        switch (this.name) {
            case GameName.KLONDIKE:
                if (game.wastePile) state.wastePile = mapTableCard(game.wastePile.cards);
                if (game.stockPile) state.stockpile = mapTableCard(game.stockPile.cards);
                this.gameStorage.setItem(mapKey, state);
                break;
            case GameName.SPIDER:
                if (game.stockPile) state.stockpile = mapTableCard(game.stockPile.cards);
                this.gameStorage.setItem(mapKey, state);
                break;
            case GameName.FREECELL:
                state.freeCells = [...game.freeCells].map((column) => mapTableCard(column.cards));
                this.gameStorage.setItem(mapKey, state);
                break;
            case GameName.PYRAMID:
                if (game.wastePile) state.wastePile = mapTableCard(game.wastePile.cards);
                if (game.stockPile) state.stockpile = mapTableCard(game.stockPile.cards);
                this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, state);
                break;
        }
        this.gameState = state;
        if (!this.tutorialActive) {
            this.gameStateBeforeTutorial = state;
        }

    }

    async clearContext() {
        const cntx: MissionContext = {
            local: {},
            common: {
                wins: 0,
                moves: 0,
                adsWatched: 0,
                magicWandUses: 0,
                time: 0,
                undos: 0,
                hints: 0,
                deckTurns: 0,
            },
        }

        const defaultContext = { [Store.name]: cntx };

        const context = await this.gameStorage.getItem(`missionContext`, defaultContext);

        this.gameStorage.setItem(`missionContext`, {...context, [Store.name]: cntx });
    }

    async setContext(newContext: Partial<MissionContext>) {

        const cntx: MissionContext = {
            local: {},
            common: {
                wins: 0,
                moves: 0,
                adsWatched: 0,
                magicWandUses: 0,
                time: 0,
                undos: 0,
                hints: 0,
                deckTurns: 0,
            },
        }

        const defaultContext = { [Store.name]: cntx };

        const context = await this.gameStorage.getItem(`missionContext`, defaultContext);

        // Merge ONLY the current game's context to avoid recursive nesting of game keys
        const existingForGame = context[Store.name] || cntx;
        const safeExistingForGame: MissionContext = {
            local: existingForGame.local || {},
            common: {
                wins: 0,
                moves: 0,
                adsWatched: 0,
                magicWandUses: 0,
                time: 0,
                undos: 0,
                hints: 0,
                deckTurns: 0,
                ...(existingForGame.common || {}),
            },
        };

        const mergedForGame = deepMerge(safeExistingForGame, newContext || {});

        const nextContext = { ...context, [Store.name]: mergedForGame };
        this.gameStorage.setItem(`missionContext`, nextContext);

        return nextContext;
    }

    async increaseContextValue(key: keyof MissionContextData, value: number = 1) {
        const cntx: MissionContext = {
            local: {},
            common: {
                wins: 0,
                moves: 0,
                adsWatched: 0,
                magicWandUses: 0,
                time: 0,
                undos: 0,
                hints: 0,
                deckTurns: 0,
                unlockedImages: 0,
            },
        }

        const defaultContext = { [Store.name]: cntx };
        const context = await this.gameStorage.getItem(`missionContext`, defaultContext);

        if (!context[Store.name]) {
            context[Store.name] = cntx;
        }

        if (context[Store.name].common === undefined) {
            context[Store.name].common = {
                wins: 0,
                moves: 0,
                adsWatched: 0,
                magicWandUses: 0,
                time: 0,
                undos: 0,
                hints: 0,
                deckTurns: 0,
                unlockedImages: 0,
            } as any;
        }
        
        (context[Store.name].common as any)[key] = ((context[Store.name].common as any)[key] || 0) + value;

        if (context[Store.name].local === undefined) {
            context[Store.name].local = {};
        }

        (context[Store.name].local as any)[key] = ((context[Store.name].local as any)[key] || 0) + value;

        // Persist only updated missionContext
        this.gameStorage.setItem(`missionContext`, context);

        return context[Store.name].local as MissionContextData;
    }

    resetTable() {
        this.gameState = emptyLastGame;
        this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, emptyLastGame);
    }

    reloadLastGame() {
        this.attempts = this.gameState.attempts;
        this.magicWandUsed = this.gameState.magicWandUsed;
        this.gameStarted = this.gameState.gameStarted;
        if (this.gameState.movesCount === 0) return;
        this.startTime = this.gameState.time;
        this.gameActive = true;
        this.movesCount = this.gameState.movesCount;
        this.score = this.gameState.score;
    }
    hasHighlighting() {
        return this.name === GameName.SPIDER || this.name === GameName.FREECELL || this.name === GameName.PYRAMID;
    }

    mockWins(event: CustomEvent) {
        if (event.detail) {
            this.wins = event.detail - 1;
            this.gameStorage.setItem('wins', this.wins);
            this.gameWins[this.name] = this.wins;
            this.gameStorage.setItem('gameWins', this.gameWins);
        }
    }
    mockDeal(event: CustomEvent) {
        if (event.detail.wins) {
            this.wins = 4;
            this.gameStorage.setItem('wins', this.wins);
            this.gameWins[this.name] = this.wins;
            this.gameWins[this.name] = this.wins;
            this.gameStorage.setItem('gameWins', this.gameWins);

            this.facebookWinPopupNextShow = FACEBOOK_FIRST_SHOW;
            this.gameStorage.setItem('facebookWinPopupNextShow', this.facebookWinPopupNextShow);
        }
        switch (this.name) {
            case GameName.FREECELL:
                this.autocompleteFreeCellMock(event.detail.check);
                break;
            case GameName.SPIDER:
                this.autocompleteSpiderMock();
                break;
            case GameName.KLONDIKE:
                this.autocompleteKlondikeMock();
                break;
            case GameName.PYRAMID:
                this.autocompletePyramidMock();
                break;
        }
    }
    autocompleteFreeCellMock(check?: string) {
        this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, freeCellGameState);
        // window.location.reload();
    }
    autocompleteSpiderMock() {
        this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, spiderGameState);
        // window.location.reload();
    }
    autocompleteKlondikeMock() {
        this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, KlondikeMockStore);
        // window.location.reload();
    }
    autocompletePyramidMock() {
        this.gameStorage.setItem(`gameState_${this.name}_${this.difficulty}`, KlondikeMockStore);
        // window.location.reload();
    }
    onVisibleChange(visible: boolean) {
        if (this.gameActive) {
            if (visible) {
                if (this.stopTime > 0) {
                    const delta = performance.now() - this.stopTime;
                    if (delta > 0) this.startTime += delta;
                    this.stopTime = 0;
                }
            } else {
                this.stopTime = performance.now();
            }
        }
    }
    hasNotification(iconName: string) {
        return this.notificationIcons.includes(iconName);
    }
    removeNotification(iconName: string) {
        this.notificationIcons = this.notificationIcons.filter((icon) => icon !== iconName);
        this.gameStorage.setItem('notificationIcons', this.notificationIcons);
    }
    async loadAdsCounter() {
        this.undoCounter = await this.gameStorage.getItem('undoCounter', DEFAULT_HINT_COUNTER);
        this.hintCounter = await this.gameStorage.getItem('hintCounter', DEFAULT_UNDO_COUNTER);
        this.wandCounter = await this.gameStorage.getItem('wandCounter', DEFAULT_WAND_COUNTER);
    }
    shownPopup(popup: string) {
        this.pauseGame();
        this.infoPopupShown.push(popup);
        this.gameStorage.setItem('infoPopupShown', this.infoPopupShown);
    }
    addNewShowedPopup(popup: string) {
        this.infoPopupShown.push(popup);
        this.gameStorage.setItem('infoPopupShown', this.infoPopupShown);
    }
    hasPopup(popup: string) {
        return this.infoPopupShown.includes(popup);
    }
    blockNotificationOnWin() {
        this.notificationWinPopupNextShow = -1;
        this.gameStorage.setItem('notificationWinPopupNextShow', this.notificationWinPopupNextShow);
    }
    showFeedbackOnWin() {
        if (
            this.wins === FEEDBACK_FIRST_SHOW || (this.wins - FEEDBACK_FIRST_SHOW) % FEEDBACK_NEXT_SHOW === 0 &&
            // this.feedbackCounterShown < FEEDBACK_SHOW_LIMIT
            !this.isFeedbackBlocked
        ) {
            this.feedbackCounterShown++;
            this.gameStorage.setItem('feedbackCounterShown', this.feedbackCounterShown);
            return true;
        }

        return false;
    }

    blockFeedbackOnWin() {
        this.isFeedbackBlocked = true;
        this.gameStorage.setItem('feedbackBlocked', this.isFeedbackBlocked);
    }

    countPopupShown(popupId: string) {
        const popupCount = this.infoPopupShown.filter((item) => item === popupId);
        return popupCount.length;
    }
    setGameWinScore(score: null | GameWinScoreData) {
        if (score) {
            this.gamesWinScore = {
                maxMoves: score.maxMoves,
                maxTime: score.maxTime,
                medianMoves: Math.min(score.medianMoves + 30, score.maxMoves),
                medianTime: Math.min(score.medianTime + 60, score.maxTime),
                minMoves: score.minMoves,
                minTime: score.minTime,
            };
        } else {
            this.gamesWinScore = null;
        }
    }
    async getTicketsAmount() {
        const tokens = await this.gameStorage.getItem('stories_tickets_counter', '0');
        if (!tokens) return 0;
        return parseInt(tokens);
    }

    getBalancedDifficulty(): DifficultyLevelType {
        const difficultyMap: { [key: string]: string } = {
            E: 'EASY',
            M: 'MEDIUM',
            H: 'HARD',
            X: 'EXPERT',
            A: 'MASTER',
        };
        // Balanced rotation patterns
        // KLONDIKE 1 CARD / FreeCell / Spider 1 Suit: EMMHMEMMXM
        const klondikeDifficultyBalanced = ['EMMHMEMMXM', 'EMMHMEMMXM'];
        // KLONDIKE 3 CARDS, SPIDER 2 SUITS: MMHMHXHMMH
        const klondike3DifficultyBalanced = ['MMHMHXHMMH', 'MMHMHXHMMH'];
        const freecellDifficultyBalanced = ['EMMHMEMMXM', 'EMMHMEMMXM'];
        const spider1DifficultyBalanced = ['EMMHMEMMXM', 'EMMHMEMMXM'];
        // SPIDER 2 SUITS pattern matches Klondike 3 cards
        const spider2DifficultyBalanced = ['MMHMHXHMMH', 'MMHMHXHMMH'];
        // SPIDER 4 SUITS: HHXHXHXHHX
        const spider4DifficultyBalanced = ['HHXHXHXHHX', 'HHXHXHXHHX'];
        const pyramidDifficultyBalanced = ['EMMHMEMMXM', 'EMMHMEMMXM'];

        const balancedIndex = this.difficultyBalancedIndex == -1 ? 0 : this.difficultyBalancedIndex;
        let diffList = '';
        const setIndex = balancedIndex < 10 ? 0 : 1;
        const gameIndex = Math.trunc(balancedIndex / 1);
        switch (this.name) {
            case GameName.KLONDIKE:
                if (this.dealCount === 1) diffList = klondikeDifficultyBalanced[setIndex];
                else diffList = klondike3DifficultyBalanced[setIndex];
                const difficultyKlondike = difficultyMap[diffList[gameIndex % diffList.length]];
                return `KLONDIKE_${difficultyKlondike}_${this.dealCount}` as DifficultyLevelType;
            case GameName.SPIDER:
                if (this.suits === 1) diffList = spider1DifficultyBalanced[setIndex];
                if (this.suits === 2) diffList = spider2DifficultyBalanced[setIndex];
                if (this.suits === 4) diffList = spider4DifficultyBalanced[setIndex];
                const difficultySpider = difficultyMap[diffList[gameIndex % diffList.length]];
                return `SPIDER_${difficultySpider}_${this.suits}` as DifficultyLevelType;

            case GameName.FREECELL:
                diffList = freecellDifficultyBalanced[setIndex];
                const difficultyFreeCell = difficultyMap[diffList[gameIndex % diffList.length]];
                return `FREECELL_${difficultyFreeCell}` as DifficultyLevelType;
            case GameName.PYRAMID:
                diffList = pyramidDifficultyBalanced[setIndex];
                const difficultyPyramid = difficultyMap[diffList[gameIndex % diffList.length]];
                return `PYRAMID_${difficultyPyramid}` as DifficultyLevelType;
        }
    }
    setTutorialViewed() {
        this.tutorialViewed[this.name] = true;
        this.gameStorage.setItem('tutorialViewed', this.tutorialViewed);
    }
    
    setTutorialGame(game: GameName | null) {
        this.tutorialGame = game;
        this.gameStorage.setItem('tutorialGame', game);
    }
    setTutorialActive(active: boolean) {
        this.setTutorialViewed();
        this.gameStorage.setItem('tutorialActive', active);
        this.gameStorage.setItem('tutorialStep', 0);
        this.setTutorialGame(this.name);
        if (this.tutorialActive !== active && !active) {
            this.gameState = this.gameStateBeforeTutorial; //gameStateBeforeTutorial is save on each change on game
        }
        this.tutorialActive = active;
        this.tutorialStep = 0;

        GameEvent.emit(active ? EventName.START_TUTORIAL : EventName.STOP_TUTORIAL);
    }
    async addTutorialWelcomeScreenShowedCount() {
        this.tutorialWelcomeScreenShowedCount++;
        this.gameStorage.setItem('tutorialWelcomeScreenShowedCount', this.tutorialWelcomeScreenShowedCount.toString());
    }
    setGameTutorialStep(step: number, tutorialState: GameData) {
        if (step === 0) {
            this.gameStorage.setItem('tutorialState', tutorialState);
            this.tutorialState = tutorialState;
        }
        this.tutorialStep = step;
        // this.gameStorage.setItem('tutorialStep', step);
        this.gameState = this.tutorialState;
    }
    get hasAutoPlay() {
        return this.autoPlay && !this.tutorialActive;
    }

    get tutorialPlacement() {
        return this.inArticle ? 'Article' : 'Game';
    }

    getMissionState(): Promise<Record<string, MissionState>> {
        return this.gameStorage.getItem('missions', '{}'); 
    }

    updateMissionState(missions: Record<string, MissionState>) {
        this.gameStorage.setItem('missions', missions); 

        return missions;
    }
}

export const Store = new GameStore();
